import { Component, LOCALE_ID, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { getTenantLetterAssets } from '../../../_shared/utils/letter-assets';
import { getTenantConfig } from '../../../_shared/utils/tenant-context';
import {
  ClarityIcons,
  angleIcon,
  minusCircleIcon,
  noteIcon,
  pencilIcon,
  windowCloseIcon
} from "@cds/core/icon";
import { DatePipe, NgForOf, NgIf } from "@angular/common";
import { ClrDatagridModule, ClrDropdownModule, ClrModalModule, ClrTabsModule, ClrTooltipModule } from "@clr/angular";
import { Delivery, DeliveryList, Personen, StatusButtons } from "../../../_shared/interfaces";
import {
  AlertService,
  FileStoreService,
  IAlertType, PersonsStoreService,
  StaffStoreService,
  TransportStoreService
} from "../../../_shared/services";
import {
  OrderTransportEditPositionsComponent
} from "./order-transport-edit-positions/order-transport-edit-positions.component";

import PackageJson from "../../../../../package.json";
import { environment as env } from "../../../../environments/environment";
import moment from "moment";

@Component({
  selector: 'app-order-transport-details',
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    ClrDropdownModule,
    ClrTabsModule,
    ClrDatagridModule,
    ClrTooltipModule,
    ClrModalModule,
    DatePipe,
    NgForOf,
    OrderTransportEditPositionsComponent
  ],
  templateUrl: './order-transport-details.component.html',
  styleUrl: './order-transport-details.component.sass',
  providers: [{ provide: LOCALE_ID, useValue: 'de'}]
})
export class OrderTransportDetailsComponent implements OnInit {
  projectId: string = '';
  jobId: number = 0;
  transportId: number = 0;
  orderId: number = 0;
  transport: Delivery[] = [];
  positions: Delivery[] = [];
  person: Personen[] = [];
  loading1: boolean = true;
  loading2: boolean = true;
  editPos: number = 0;  // 0 = Anzeige; 1 = Änderungsmodus, 2 = Position ändern
  statusLabel = 'label-light-blue';
  statusText = '';
  staffFirstName: string = '';
  staffLastName: string = '';
  staffEMail: string = '';
  staffPhone: string = '';
  positionId: number = 0;
  partId: number = 0;
  selected: DeliveryList[] = [];

  public ppsVersion: string = PackageJson.version;

  statusButtons: StatusButtons[] = [
    { state: 'running', display: 'In Arbeit',           label: 'alert-info',    icon: 'info-circle'},
    { state: 'waiting', display: 'Wartet auf Abholung', label: 'alert-warning', icon: 'exclamation-triangle'},
    { state: 'ontour',  display: 'On Tour',             label: 'alert-danger',  icon: 'exclamation-circle'},
    { state: 'closed',  display: 'Empfang bestätigt',   label: 'alert-success', icon: 'check-circle'},
  ];

  // Für Ausdruck
  basic = false;
  clrModalSize: string = 'xl';
  devMode: boolean;
  private readonly tenantConfig = getTenantConfig();

  documentDefinition = getTenantLetterAssets().a4Letter;

  constructor(
    private transs: TransportStoreService,
    private filess: FileStoreService,
    private staffss: StaffStoreService,
    private persss: PersonsStoreService,
    private router: Router,
    private alertDispatcher: AlertService,
    private route: ActivatedRoute
  ) {
    this.devMode = env.stages.dev;
    ClarityIcons.addIcons(pencilIcon, angleIcon, noteIcon, windowCloseIcon, minusCircleIcon)
  }

  ngOnInit(): void {
    const params = this.route.snapshot.paramMap;
    this.projectId = params.get('prid') ?? '';
    this.jobId = parseInt(params.get('jobid') ?? '0', 10);
    this.transportId = parseInt(params.get('transportid') ?? '0', 10);
    this.orderId = parseInt(params.get('orderid') ?? '0', 10);
    this.loading1 = true;
    this.loading2 = true;
    this.editPos = 0;
    this.clrModalSize = 'xl';
    this.loadTransportNote(this.projectId, this.transportId);
    this.loadTransportPositions();
  }

  // Anpassung Farbe und Text des Statusbuttons...
  findButtonStatus(orderstatus: string, buttons: StatusButtons[]) {
    const idx = buttons.findIndex(el => el.state === orderstatus);
    return idx >= 0 ? idx : 0;
  };

  splitProjectNumber(projectId: string, substring: number) {
    var splits = projectId.split('.');
    return splits[substring];
  }

  loadTransportNote(projectId: string, transportId: number) {
    this.transs.getTransport(projectId, 0, transportId, 'all').subscribe({
      next: data1 => {
        if (!data1 || !data1.object) {
          this.alertDispatcher.show({
            message: 'Keine Orderdaten gefunden!',
            clrAlertType: IAlertType.WARNING
          }, 5000);
          return;
        }
        this.transport = data1.object;
      },
      complete: () => {
        // Mitarbeiter personalisieren
        this.staffStatus(this.transport[0].employee_phm);

        // Person ermitteln
        this.loadPersonData(this.transport[0].employee_leader);

        // Status gestalten
        const statusline = this.findButtonStatus(this.transport[0].status, this.statusButtons);
        if (statusline >= 0) {
          this.statusLabel = this.statusButtons[statusline].label;
          this.statusText = this.statusButtons[statusline].display;
        }

        this.loading1 = false;
      },
      error: () => {
        return
      }
    });
  }

  loadTransportPositions() {
    this.transs.getTransportPositions(this.projectId, this.transportId, 0, 0).subscribe({
      next: data2 => {
        if (!data2 || !data2.object) {
          this.alertDispatcher.show({
            message: 'Keine Orderpositionen gefunden!',
            clrAlertType: IAlertType.WARNING
          }, 5000);
          return;
        }
        this.positions = data2.object;
      },
      complete: () => {
        this.loading2 = false;
      },
      error: () => {
        return
      }
    });
  }

  deleteTransport(projectId: string, transportId: number) {
    if (confirm('Wollen Sie diesen Transportschein wirklich löschen?\nDiese Aktion kann nicht rückgängig gemacht werden!')) {
      this.transs.deleteTransportNote(projectId, transportId)
        .subscribe({
          next: () => {
          },
          complete: () => { this.router.navigate(['/project/details/', this.splitProjectNumber(projectId, 0), this.splitProjectNumber(projectId, 1)]);
          },
          error: () => {
            return
          }
        });
    }
  }

  updateTransport(done: boolean) {
    this.editPos = 1;
    this.loadTransportNote(this.projectId, this.transportId);
    this.loadTransportPositions();
  }

  changeTransportStatus(transportID: number, status: number){
    if (confirm('Wollen Sie den Status dieses Transportscheins wirklich auf "' + this.statusButtons[status].display + '" setzen?')) {
      this.transs.updateTransportStatus(this.projectId, transportID, this.statusButtons[status].state)
        .subscribe(data => {
          this.loadTransportNote(this.projectId, transportID);
          this.loadTransportPositions();
          this.router.navigate(['/transport/details/', this.projectId, this.jobId, transportID, this.orderId]);
        });
    }
  }

  staffStatus(staffId: number){
    this.staffss.getStaffById(staffId)
      .subscribe({
        next: staff => {
          if (!staff) {
            return;
          }
          this.staffFirstName = staff.object[0].firstname;
          this.staffLastName = staff.object[0].lastname;
          this.staffEMail = staff.object[0].email;
          this.staffPhone = staff.object[0].phone;
        },
        complete: () => {
          if (this.devMode) console.log('StaffID ist ' + staffId);
        }
      })
  }

  loadPersonData(person: number) {
    this.persss.getSinglePersons(person)
      .subscribe({
        next: res => {
          this.person = res.object
        },
        complete: () => {},
        error: () => {
          return
        }
      })
  }

  updatePositionMode(index: number, positionId: number, partId: number) {
    this.editPos = 2;
    this.positionId = positionId;
    this.partId = partId;
  }

  deletePosition(projectId: string, transportId: number, positionId: string, partId: number) {
    this.transs.deleteTransportPosition(projectId, transportId, positionId, partId)
      .subscribe({
        next: () => {
          this.loadTransportPositions();
        },
        complete: () => {
          this.alertDispatcher.show({
            message: ' Position ' + positionId + '.' + partId + ' wurde gelöscht. ',
            clrAlertType: IAlertType.INFO
          }, 2000);
        },
        error: () => {
          return
        }
      });
  }

  createTransportNote(positions : any[]) {
    let invoiceValue = 0.00;
    let benennung = '';
    let posTable = [[{ text: 'Pos.'}, { text: 'Bezeichnung'}, { text: 'Zeichnung', colSpan: 1, style: '' }, { text: 'Material'}, { text: 'Kennzeichnung'}, { text: '' }, { text: 'Anzahl'}]];

    positions.forEach(position => {
      benennung = position.name;
      if (position.description) benennung += '\n(' + position.description + ')';
      if (position.taric && position.taric !== "0") benennung += '\n(Zolltarifnummer: ' + position.taric + ')';
      if (position.heat_nr == 'null') position.heat_nr = '';
      if (position.serial_nr == 'null') position.serial_nr = '';
      if (position.controlling_nr == 'null') position.controlling_nr = '';
      if (position.heat_nr) position.material += '\n' + position.heat_nr;
      if (position.controlling_nr) position.material += '\n' + position.controlling_nr;

      switch(position.part) {
        case 0: {
          if (position.technology) {
            posTable.push(
              [ position.position, benennung, position.technical_drawing, position.material, position.serial_nr, '', '' ]
            );
          } else {
            posTable.push(
              [ position.position, benennung, position.technical_drawing, position.material, position.serial_nr, '', position.number_of_pieces  ]
            );
          }
          break;
        }
        default: {
          if (position.technology) {
            posTable.push(
              [ {
                text: position.position + '.' + position.part,
                margin: [10, 0]
              }, benennung, position.technical_drawing, position.material, position.serial_nr, '', ''  ]
            );
          } else {
            posTable.push(
              [ {
                text: position.position + '.' + position.part,
                margin: [10, 0]
              }, benennung, position.technical_drawing, position.material, position.serial_nr, '', position.number_of_pieces  ]
            );
          }
          break;
        }
      }
      if (position.remarks && position.remarks != 'null') {
        posTable.push(
          [ { text: ""}, { text: ""}, { text: position.remarks, colSpan: 4, style: '' }, { text: ""}, { text: ""}, { text: ""}, { text: ""} ]
        )}
    });

    this.documentDefinition.info.title = 'PPS Transportschein';
    this.documentDefinition.info.subject = 'TRANSPORTSCHEIN ' + this.projectId + '.0005.' + this.transportId;
    this.documentDefinition.info.revison = this.ppsVersion;

    (this.documentDefinition.content[0] as any).columns[0].text = this.transport[0].company_name + '\n' + this.transport[0].street + '\n' + this.transport[0].postcode + ' ' + this.transport[0].city;
    if(this.transport[0].country && (this.transport[0].country != 'Deutschland')) {
      (this.documentDefinition.content[0] as any).columns[0].text += '\n' + this.transport[0].country
    }
    if (this.transport[0].employee_leader && this.person?.[0]) {
      (this.documentDefinition.content[0] as any).columns[0].text += '\n\n' + this.person[0].salutation + ' ' + this.person[0].lastname;
      (this.documentDefinition.content[0] as any).columns[0].text += '\n' + this.person[0].phone;
    }

    (this.documentDefinition.content[0] as any).columns[2].text = this.tenantConfig.letterCity + ', ' + moment(this.transport[0].delivery_date).format("DD.MM.yyyy") + '\n\nIhr Ansprechpartner:\n' + this.staffFirstName + ' ' + this.staffLastName + '\n' + this.staffEMail;
    this.documentDefinition.content[1].text = 'TRANSPORTSCHEIN ' + this.projectId + '.0005.' + this.transportId ;
    this.documentDefinition.content[2].text = 'gemäß Auftrag: ' + this.projectId + '.0002.' +  this.orderId ;
    if(this.transport[0].order_confirmation_id) {
      this.documentDefinition.content[2].text += '\n' + 'gemäß Auftragsbestätigung: ' + this.transport[0].order_confirmation_id ;
    }

    this.documentDefinition.content[3].text = '';
    (this.documentDefinition.content[4] as any).table.headerRows = 1;
    (this.documentDefinition.content[4] as any).table.widths = [ 'auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'];
    (this.documentDefinition.content[4] as any).table.body = posTable;

    this.documentDefinition.content[5] = {
      stack: [
        { text: 'Ware ordnungsgemäß und in angegebener Stückzahl erhalten.', style: 'fett', margin: [30, 10] },
        {
          layout: 'Borders',
            table: {
          // headers are automatically repeated if the table spans over multiple pages
          // you can declare how many rows should be treated as headers
          headerRows: 1,
            widths: [ '50%', '50%' ],

            body: [
            [ { text: 'Warenempfangsbestätigung/Speditionsübergabebestätigung', style: 'fett', colSpan: 2, margin: [0, 4]},'' ],
            [ [ { text: 'Name der übernehmenden Firma:'},{ text: '(Wareneingangsstempel)', style: 'small'} ], '' ],
            [ [ { text: 'Fahrzeugkennzeichen:'},{ text: '(bei Fremdspedition)', style: 'small'} ], '' ],
            [ [ { text: 'Datum:', margin: [0, 4] } ], '' ],
            [ [ { text: 'Name in Druckschrift:', margin: [0, 4] } ], '' ],
            [ [ { text: 'Unterschrift:', margin: [0, 4] } ], '' ]
          ]
        },
          style: 'normal',
            margin: [30, 10]
        },
        { text: 'Mit der Unterschrift wird die ordnungsgemäße Ladungssicherung anerkannt.', style: 'normal', margin: [30, 10] }
      ]
    } as any;
    this.documentDefinition.content.splice(6);

    this.generatePdf();
  }

  openPdfPreview() {
    this.basic = true;
    // Wait until the modal body exists in the DOM before injecting the iframe.
    setTimeout(() => this.createTransportNote(this.positions), 0);
  }

  generatePdf(){
    const documentGenerator = pdfMake.createPdf(this.documentDefinition as any);

    documentGenerator.getDataUrl((dataUrl: string) => {
      const targetElement = document.querySelector('#documentContainer');
      if (!targetElement) {
        this.alertDispatcher.alertConfig.message = 'PDF-Container nicht gefunden!';
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
        return; // Exit the function if the element doesn't exist
      }
      targetElement.innerHTML = '';

      const pdfIframe = document.createElement('iframe');
      pdfIframe.src = dataUrl;
      pdfIframe.width = '100%';
      pdfIframe.height = '100%';
      targetElement.appendChild(pdfIframe);
    })
  }

  archivePdf() {
    if (confirm('Wollen Sie die Datei ' + 'Transport_Note_' + this.projectId + '.0005.' + this.transportId + '.pdf archivieren?')) {
      this.safePdf();
    }

    this.alertDispatcher.show({
      message: ' Datei ' + 'Transport_Note_' + this.projectId + '.0005.' + this.transportId + '.pdf wurde archiviert.',
      clrAlertType: IAlertType.SUCCESS
    }, 5000);
  }

  async downloadPdf(){
    if (confirm('Wollen Sie die Datei ' + 'Transport_Note_' + this.projectId + '.0005.' + this.transportId + '.pdf wirklich herunterladen?')) {
      const documentGenerator = pdfMake.createPdf(this.documentDefinition).download('Transport_Note_' +  this.projectId + '.0005.' + this.transportId + '.pdf');
    }

    await this.safePdf();

    this.alertDispatcher.alertConfig.message = 'Datei ' + 'Transport_Note_' + this.projectId + '.0005.' + this.transportId + '.pdf wurde herunterladen.';
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
  }

  async printPdf() {
    if (confirm('Wollen Sie die Datei ' + 'Transport_Note_' + this.projectId + '.0005.' + this.transportId + '.pdf ausdrucken?')) {
      pdfMake.createPdf(this.documentDefinition).print();
    }

    await this.safePdf()

    this.alertDispatcher.show({
      message: ' Datei ' + 'Transport_Note_' + this.projectId + '.0005.' + this.transportId + '.pdf wurde an den Drucker übergeben. ',
      clrAlertType: IAlertType.INFO
    }, 2000);
  }

  safePdf() {
    const documentGenerator = pdfMake.createPdf(this.documentDefinition as any);
    documentGenerator.getBlob((blob: Blob) => {
      // Convert Blob to File
      const file = new File(
        [blob], // Blob content
        'Transport_Note_' + this.projectId + '.0005.' + this.transportId + '.pdf',
        { type: blob.type, lastModified: Date.now() } // File metadata
      );

      // Now, send the file instead of the Blob
      this.filess.sendFile(file, 4, this.orderId, this.projectId, this.jobId, '', 'Transport_Note_' + this.projectId + '.0005.' + this.transportId)
        .subscribe({
          next: (data) =>
          {
            if (this.devMode) console.log("done")
          },
          complete: () => {
            this.alertDispatcher.show({
              message: ' Datei ' + 'Transport_Note_' + this.projectId + '.0005.' + this.transportId + '.pdf wurde gespeichert.',
              clrAlertType: IAlertType.SUCCESS
            }, 5000);
          },
          error: (error) => console.error("Error:", error)
        });
    });
  }

  onDelete() {
    let choosenPositions = this.selected;
    if (confirm('Wollen Sie die ausgewählten Positionen tatsächlich löschen?')) {
      choosenPositions.forEach(array => {
        this.deletePosition(this.projectId, this.transportId, array.position.toString() ?? '', array.part ?? 0)
      });

      this.loadTransportNote(this.projectId, this.transportId);
      this.loadTransportPositions();

      this.alertDispatcher.alertConfig.message = 'Die Positionen wurden gelöscht.';
      this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
    }
  }
}
